import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

// ═══════════════════════════════════════
// Import Process Status Stages
// ═══════════════════════════════════════
export type RequestStatus =
  | 'received'
  | 'searching'
  | 'auction'
  | 'purchased'
  | 'shipping'
  | 'customs'
  | 'delivered'

export const STATUS_LABELS: Record<RequestStatus, string> = {
  received: 'Solicitud Recibida',
  searching: 'Buscando en Subasta',
  auction: 'En Subasta',
  purchased: 'Comprado',
  shipping: 'En Transporte',
  customs: 'En Aduana',
  delivered: 'Entregado',
}

export interface CarRequestData {
  car: {
    year: number
    make: string
    model: string
    trim: string
    image: string
  }
  priceRange: {
    min: number
    max: number
  }
}

// Matches the Supabase table structure
export interface CarRequest extends CarRequestData {
  id: string
  user_id?: string
  email: string
  phone: string
  name: string
  status: RequestStatus
  statusLabel: string
  created_at: string
  message?: string
}

export function useUserRequests() {
  const [requests, setRequests] = useState<CarRequest[]>([])
  const [loading, setLoading] = useState(true)
  const { user } = useAuth()

  const fetchRequests = useCallback(async () => {
    if (!user) {
      setRequests([])
      setLoading(false)
      return
    }

    try {
      const { data, error } = await supabase
        .from('requests')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      // Transform DB data to frontend shape
      const formattedRequests: CarRequest[] = (data || []).map((item: any) => ({
        id: item.id,
        user_id: item.user_id,
        email: item.email,
        phone: item.phone,
        name: item.name,
        message: item.message,
        created_at: item.created_at,
        status: item.status as RequestStatus,
        statusLabel: STATUS_LABELS[item.status as RequestStatus] || item.status,
        car: {
          year: item.car_year,
          make: item.car_make,
          model: item.car_model,
          trim: item.car_trim,
          image: item.car_image,
        },
        priceRange: {
          min: item.price_min,
          max: item.price_max,
        },
      }))

      setRequests(formattedRequests)
    } catch (err) {
      console.error('Error fetching requests:', err)
    } finally {
      setLoading(false)
    }
  }, [user])

  // Initial fetch
  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  // Subscribe to changes (Realtime)
  useEffect(() => {
    if (!user) return

    const subscription = supabase
      .channel('requests_channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'requests' },
        () => {
          fetchRequests()
        },
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [user, fetchRequests])

  const saveRequest = useCallback(
    async (
      data: CarRequestData,
      contactInfo: {
        name: string
        email: string
        phone: string
        message: string
      },
    ) => {
      if (!user) {
        // If not logged in, we can't save to DB with the current RLS policies
        // You might want to prompt login or allow anon inserts in Supabase
        alert('Por favor inicia sesión para guardar tu solicitud.')
        return null
      }

      try {
        const { data: insertedData, error } = await supabase
          .from('requests')
          .insert([
            {
              user_id: user.id,
              email: contactInfo.email,
              phone: contactInfo.phone,
              name: contactInfo.name,
              message: contactInfo.message,
              car_year: data.car.year,
              car_make: data.car.make,
              car_model: data.car.model,
              car_trim: data.car.trim,
              car_image: data.car.image,
              price_min: data.priceRange.min,
              price_max: data.priceRange.max,
              status: 'received',
            },
          ])
          .select()

        if (error) throw error
        return insertedData
      } catch (err) {
        console.error('Error saving request:', err)
        throw err
      }
    },
    [user],
  )

  const updateStatus = useCallback(
    async (id: string, newStatus: RequestStatus) => {
      try {
        const { error } = await supabase
          .from('requests')
          .update({ status: newStatus })
          .eq('id', id)

        if (error) throw error

        // Update local state immediately
        setRequests((prev) =>
          prev.map((r) =>
            r.id === id
              ? {
                  ...r,
                  status: newStatus,
                  statusLabel: STATUS_LABELS[newStatus],
                }
              : r,
          ),
        )
        return true
      } catch (err) {
        console.error('Error updating status:', err)
        return false
      }
    },
    [],
  )

  const deleteRequest = useCallback(async (id: string) => {
    try {
      const { error } = await supabase.from('requests').delete().eq('id', id)

      if (error) throw error

      // Remove from local state
      setRequests((prev) => prev.filter((r) => r.id !== id))
      return true
    } catch (err) {
      console.error('Error deleting request:', err)
      return false
    }
  }, [])

  return {
    requests,
    loading,
    saveRequest,
    updateStatus,
    deleteRequest,
    refreshRequests: fetchRequests,
  }
}
